import React, { useState, useEffect, useRef } from 'react';
import { Save, Printer, DollarSign, RotateCcw, List } from 'lucide-react';
import { PatientSearch } from '../../components/pharmacy-pos/PatientSearch';
import { TypeSwitch, IPDFields, OPDFields, WalkInFields } from '../../components/pharmacy-pos/TypeSwitch';
import { MedicineTable } from '../../components/pharmacy-pos/MedicineTable';
import { Cart } from '../../components/pharmacy-pos/Cart';
import { PaymentModal } from '../../components/pharmacy-pos/PaymentModal';
import { PrintTemplate80mm } from '../../components/pharmacy-pos/PrintTemplate80mm';
import { PrintTemplateA4 } from '../../components/pharmacy-pos/PrintTemplateA4';
import { pharmacyPOSService } from '../../services/pharmacy-pos.service';
import type {
  SaleType,
  PatientSearchResult,
  Medicine,
  CartItem,
  DiscountMode,
  PaymentMethod,
  PharmacySale,
  HeldSale
} from '../../types/pharmacy-pos';
import toast from 'react-hot-toast';

export const PharmacyPOS: React.FC = () => {
  const [saleType, setSaleType] = useState<SaleType>('walkin');
  const [selectedPatient, setSelectedPatient] = useState<PatientSearchResult | null>(null);

  const [admissionId, setAdmissionId] = useState('');
  const [bedId, setBedId] = useState('');
  const [encounterId, setEncounterId] = useState('');

  const [doctorId, setDoctorId] = useState('');
  const [visitNo, setVisitNo] = useState('');
  const [prescriptionNo, setPrescriptionNo] = useState('');

  const [walkInName, setWalkInName] = useState('');
  const [walkInPhone, setWalkInPhone] = useState('');
  const [walkInAddress, setWalkInAddress] = useState('');

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [discountMode, setDiscountMode] = useState<DiscountMode>('percent');
  const [discountValue, setDiscountValue] = useState(0);
  const [vatPercent, setVatPercent] = useState(0);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showHeldSalesModal, setShowHeldSalesModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [printSale, setPrintSale] = useState<PharmacySale | null>(null);
  const [printMode, setPrintMode] = useState<'80mm' | 'a4'>('80mm');
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2') {
        e.preventDefault();
        handleNewSale();
      }
      if (e.key === 'F4') {
        e.preventDefault();
        handleHoldSale();
      }
      if (e.key === 'F10') {
        e.preventDefault();
        if (cartItems.length > 0) {
          setShowPaymentModal(true);
        }
      }
      if (e.key === 'Delete' && e.ctrlKey) {
        e.preventDefault();
        if (confirm('Clear all items from cart?')) {
          setCartItems([]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cartItems]);

  const handleSaleTypeChange = (type: SaleType) => {
    setSaleType(type);
    if (type !== 'ipd') {
      setAdmissionId('');
      setBedId('');
      setEncounterId('');
    }
    if (type !== 'opd') {
      setDoctorId('');
      setVisitNo('');
      setPrescriptionNo('');
    }
    if (type !== 'walkin') {
      setWalkInName('');
      setWalkInPhone('');
      setWalkInAddress('');
    }
  };

  const handleAddToCart = (medicine: Medicine) => {
    const existingIndex = cartItems.findIndex(item => item.medicine.id === medicine.id);

    if (existingIndex >= 0) {
      const existingItem = cartItems[existingIndex];
      if (existingItem.quantity >= medicine.stock) {
        toast.error('Cannot add more than available stock');
        return;
      }
      const newItems = [...cartItems];
      newItems[existingIndex] = {
        ...existingItem,
        quantity: existingItem.quantity + 1,
        line_total: (existingItem.quantity + 1) * existingItem.unit_price
      };
      setCartItems(newItems);
      toast.success('Quantity increased');
    } else {
      const newItem: CartItem = {
        medicine,
        quantity: 1,
        unit_price: medicine.mrp,
        line_total: medicine.mrp
      };
      setCartItems([...cartItems, newItem]);
      toast.success('Medicine added to cart');
    }
  };

  const handleQuantityChange = (index: number, quantity: number) => {
    const item = cartItems[index];
    if (quantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }
    if (quantity > item.medicine.stock) {
      toast.error('Cannot exceed available stock');
      return;
    }

    const newItems = [...cartItems];
    newItems[index] = {
      ...item,
      quantity,
      line_total: quantity * item.unit_price
    };
    setCartItems(newItems);
  };

  const handleRemoveItem = (index: number) => {
    setCartItems(cartItems.filter((_, i) => i !== index));
    toast.success('Item removed from cart');
  };

  const handleNewSale = () => {
    if (cartItems.length > 0 && !confirm('Clear current sale and start new?')) {
      return;
    }
    setSaleType('walkin');
    setSelectedPatient(null);
    setCartItems([]);
    setDiscountValue(0);
    setVatPercent(0);
    setAdmissionId('');
    setBedId('');
    setEncounterId('');
    setDoctorId('');
    setVisitNo('');
    setPrescriptionNo('');
    setWalkInName('');
    setWalkInPhone('');
    setWalkInAddress('');
    toast.success('New sale started');
  };

  const handleHoldSale = async () => {
    if (cartItems.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    if (saleType === 'ipd' && (!admissionId || !bedId)) {
      toast.error('Admission No and Bed No are required for IPD');
      return;
    }

    setProcessing(true);
    try {
      const saleData = {
        sale_type: saleType,
        patient_id: selectedPatient?.id,
        patient_uhid: selectedPatient?.uhid,
        patient_name: saleType === 'walkin' ? walkInName : selectedPatient?.name,
        patient_phone: saleType === 'walkin' ? walkInPhone : selectedPatient?.phone,
        patient_address: saleType === 'walkin' ? walkInAddress : selectedPatient?.address,
        ipd_encounter_id: saleType === 'ipd' ? encounterId : undefined,
        admission_id: saleType === 'ipd' ? admissionId : undefined,
        bed_id: saleType === 'ipd' ? bedId : undefined,
        doctor_id: saleType === 'opd' ? doctorId : undefined,
        visit_no: saleType === 'opd' ? visitNo : undefined,
        prescription_no: saleType === 'opd' ? prescriptionNo : undefined,
        items: cartItems.map(item => ({
          medicine_id: item.medicine.id,
          medicine_name: item.medicine.name,
          generic_name: item.medicine.generic_name,
          batch_no: item.medicine.batch_no,
          expiry_date: item.medicine.expiry_date,
          quantity: item.quantity,
          unit_price: item.unit_price,
          mrp: item.medicine.mrp
        })),
        discount_mode: discountMode,
        discount_value: discountValue,
        vat_percent: vatPercent,
        status: 'held' as const
      };

      await pharmacyPOSService.createSale(saleData);
      toast.success('Sale held successfully');
      handleNewSale();
    } catch (error) {
      console.error('Error holding sale:', error);
      toast.error('Failed to hold sale');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = async (paymentMethod: PaymentMethod, amountReceived: number) => {
    if (cartItems.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    if (saleType === 'ipd' && (!admissionId || !bedId)) {
      toast.error('Admission No and Bed No are required for IPD');
      return;
    }

    if (saleType !== 'walkin' && !selectedPatient) {
      toast.error('Please select a patient');
      return;
    }

    setProcessing(true);
    try {
      const saleData = {
        sale_type: saleType,
        patient_id: selectedPatient?.id,
        patient_uhid: selectedPatient?.uhid,
        patient_name: saleType === 'walkin' ? walkInName : selectedPatient?.name,
        patient_phone: saleType === 'walkin' ? walkInPhone : selectedPatient?.phone,
        patient_address: saleType === 'walkin' ? walkInAddress : selectedPatient?.address,
        ipd_encounter_id: saleType === 'ipd' ? encounterId : undefined,
        admission_id: saleType === 'ipd' ? admissionId : undefined,
        bed_id: saleType === 'ipd' ? bedId : undefined,
        doctor_id: saleType === 'opd' ? doctorId : undefined,
        visit_no: saleType === 'opd' ? visitNo : undefined,
        prescription_no: saleType === 'opd' ? prescriptionNo : undefined,
        items: cartItems.map(item => ({
          medicine_id: item.medicine.id,
          medicine_name: item.medicine.name,
          generic_name: item.medicine.generic_name,
          batch_no: item.medicine.batch_no,
          expiry_date: item.medicine.expiry_date,
          quantity: item.quantity,
          unit_price: item.unit_price,
          mrp: item.medicine.mrp
        })),
        discount_mode: discountMode,
        discount_value: discountValue,
        vat_percent: vatPercent,
        payment_method: paymentMethod,
        amount_received: amountReceived,
        status: 'paid' as const
      };

      const result = await pharmacyPOSService.createSale(saleData);
      toast.success(`Sale completed! Invoice: ${result.invoice_no}`);
      
      const sale = await pharmacyPOSService.getSaleById(result.sale.id);
      if (sale) {
        setPrintSale(sale);
        setTimeout(() => {
          window.print();
        }, 500);
      }

      setShowPaymentModal(false);
      handleNewSale();
    } catch (error: any) {
      console.error('Error processing payment:', error);
      toast.error(error.message || 'Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };

  const calculateGrandTotal = (): number => {
    const subtotal = cartItems.reduce((sum, item) => sum + item.line_total, 0);
    let discount_amount = 0;
    if (discountMode === 'percent') {
      discount_amount = subtotal * (discountValue / 100);
    } else {
      discount_amount = discountValue;
    }
    const vat_amount = (subtotal - discount_amount) * (vatPercent / 100);
    return subtotal - discount_amount + vat_amount;
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Header */}
      <div className="bg-green-600 text-white rounded-lg shadow-md p-4 mb-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Medicine Sell</h1>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowHeldSalesModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-white text-green-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <List className="w-4 h-4" />
              <span>Held Sales</span>
            </button>
            <button
              onClick={handleNewSale}
              className="flex items-center space-x-2 px-4 py-2 bg-white text-green-600 rounded-lg hover:bg-gray-100 transition-colors"
              title="F2"
            >
              <RotateCcw className="w-4 h-4" />
              <span>New Sale</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-4 mb-4">
        {/* Left Column - Medicine Table */}
        <div className="h-[calc(100vh-200px)]">
          <MedicineTable onAddToCart={handleAddToCart} disabled={processing} />
        </div>

        {/* Right Column - Customer & Cart */}
        <div className="space-y-4">
          {/* Customer Type & Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4">
            <TypeSwitch
              selectedType={saleType}
              onTypeChange={handleSaleTypeChange}
              disabled={processing}
            />

            {saleType !== 'walkin' && (
              <PatientSearch
                onPatientSelect={setSelectedPatient}
                selectedPatient={selectedPatient}
                disabled={processing}
              />
            )}

            {saleType === 'ipd' && (
              <IPDFields
                admissionId={admissionId}
                bedId={bedId}
                encounterId={encounterId}
                onAdmissionChange={setAdmissionId}
                onBedChange={setBedId}
                onEncounterChange={setEncounterId}
                disabled={processing}
              />
            )}

            {saleType === 'opd' && (
              <OPDFields
                doctorId={doctorId}
                visitNo={visitNo}
                prescriptionNo={prescriptionNo}
                onDoctorChange={setDoctorId}
                onVisitNoChange={setVisitNo}
                onPrescriptionNoChange={setPrescriptionNo}
                disabled={processing}
              />
            )}

            {saleType === 'walkin' && (
              <WalkInFields
                name={walkInName}
                phone={walkInPhone}
                address={walkInAddress}
                onNameChange={setWalkInName}
                onPhoneChange={setWalkInPhone}
                onAddressChange={setWalkInAddress}
                disabled={processing}
              />
            )}
          </div>

          {/* Cart */}
          <div className="h-[calc(100vh-500px)]">
            <Cart
              items={cartItems}
              discountMode={discountMode}
              discountValue={discountValue}
              vatPercent={vatPercent}
              onQuantityChange={handleQuantityChange}
              onRemoveItem={handleRemoveItem}
              onDiscountModeChange={setDiscountMode}
              onDiscountValueChange={setDiscountValue}
              onVatPercentChange={setVatPercent}
              disabled={processing}
            />
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={handleHoldSale}
              disabled={processing || cartItems.length === 0}
              className="flex items-center justify-center space-x-2 px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="F4"
            >
              <Save className="w-5 h-5" />
              <span>Hold</span>
            </button>

            <button
              onClick={() => setPrintSale(printSale)}
              disabled={!printSale}
              className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Printer className="w-5 h-5" />
              <span>Print</span>
            </button>

            <button
              onClick={() => setShowPaymentModal(true)}
              disabled={processing || cartItems.length === 0}
              className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="F10"
            >
              <DollarSign className="w-5 h-5" />
              <span>Pay</span>
            </button>
          </div>
        </div>
      </div>

      {/* Keyboard Shortcuts Help */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
          <span><kbd className="px-2 py-1 bg-gray-100 rounded">F2</kbd> New Sale</span>
          <span><kbd className="px-2 py-1 bg-gray-100 rounded">F4</kbd> Hold</span>
          <span><kbd className="px-2 py-1 bg-gray-100 rounded">F10</kbd> Pay</span>
          <span><kbd className="px-2 py-1 bg-gray-100 rounded">Enter</kbd> Add (on medicine row)</span>
          <span><kbd className="px-2 py-1 bg-gray-100 rounded">Ctrl+Del</kbd> Clear Cart</span>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        grandTotal={calculateGrandTotal()}
        onConfirmPayment={handlePayment}
        loading={processing}
      />

      {/* Print Templates (Hidden) */}
      {printSale && (
        <div ref={printRef} className="hidden print:block">
          {printMode === '80mm' ? (
            <PrintTemplate80mm sale={printSale} />
          ) : (
            <PrintTemplateA4 sale={printSale} />
          )}
        </div>
      )}
    </div>
  );
};

export default PharmacyPOS;
